import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SiteProvider, useSite } from "./contexts/SiteContext";
import { CartProvider } from "./contexts/CartContext";
import { LocaleProvider } from "./contexts/LocaleContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { ThemeProvider } from "./contexts/ThemeContext";

import Header from "./components/Header";
import Footer from "./components/Footer";
import ChatbotWidget from "./components/ChatbotWidget";
import DiscountPopup from "./components/DiscountPopup";
import MaintenanceOverlay from "./components/MaintenanceOverlay";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import TrackPage from "./pages/TrackPage";
import About from "./pages/About";
import Contact from "./pages/Contact";
import CustomPage from "./pages/CustomPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import StaffLogin from "./pages/StaffLogin";

import DashboardLayout from "./components/DashboardLayout";
import DevOverview from "./pages/dev/Overview";
import DevKeys from "./pages/dev/Keys";
import DevAIUsage from "./pages/dev/AIUsage";
import DevRoles from "./pages/dev/Roles";
import DevUsers from "./pages/dev/Users";
import DevBranding from "./pages/dev/Branding";
import DevContent from "./pages/dev/Content";
import DevPages from "./pages/dev/Pages";
import DevMaintenance from "./pages/dev/Maintenance";
import DevSupport from "./pages/dev/Support";
import DevSecurity from "./pages/dev/Security";
import DevAudit from "./pages/dev/Audit";
import DevLimits from "./pages/dev/Limits";
import DevHomepage from "./pages/dev/Homepage";
import DevMenus from "./pages/dev/Menus";

import AdmOverview from "./pages/admin/Overview";
import AdmOrders from "./pages/admin/Orders";
import AdmProducts from "./pages/admin/Products";
import AdmCustomers from "./pages/admin/Customers";
import AdmReviews from "./pages/admin/Reviews";
import AdmEnquiries from "./pages/admin/Enquiries";
import AdmRefunds from "./pages/admin/Refunds";
import AdmDiscounts from "./pages/admin/Discounts";
import AdmDeveloperSupport from "./pages/admin/DeveloperSupport";
import AdmApprovals from "./pages/admin/Approvals";
import AdmCategories from "./pages/admin/Categories";
import AdmPopup from "./pages/admin/Popup";

import ShopOverview from "./pages/shop/Overview";
import ShopAddProduct from "./pages/shop/AddProduct";
import ShopAddProductManual from "./pages/shop/AddProductManual";
import ShopCameraForm from "./pages/shop/CameraForm";
import ShopMyProducts from "./pages/shop/MyProducts";
import Wishlist from "./pages/Wishlist";
import "./App.css";

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/40">Loading…</div>;
  if (!user) return <Navigate to="/staff/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const StorefrontShell = ({ children }) => (
  <>
    <Header />
    <main className="min-h-[60vh]">{children}</main>
    <Footer />
    <ChatbotWidget />
    <DiscountPopup />
    <MaintenanceOverlay />
  </>
);

function AppRoutes() {
  const { settings } = useSite();
  return (
    <Routes>
      <Route path="/" element={<StorefrontShell><Home /></StorefrontShell>} />
      <Route path="/shop" element={<StorefrontShell><Shop /></StorefrontShell>} />
      <Route path="/product/:id" element={<StorefrontShell><ProductDetail /></StorefrontShell>} />
      <Route path="/cart" element={<StorefrontShell><Cart /></StorefrontShell>} />
      <Route path="/checkout" element={<StorefrontShell><Checkout /></StorefrontShell>} />
      <Route path="/order/:id" element={<StorefrontShell><OrderConfirmation /></StorefrontShell>} />
      <Route path="/track" element={<StorefrontShell><TrackPage /></StorefrontShell>} />
      <Route path="/wishlist" element={<StorefrontShell><Wishlist /></StorefrontShell>} />
      <Route path="/about" element={<StorefrontShell><About /></StorefrontShell>} />
      <Route path="/contact" element={<StorefrontShell><Contact /></StorefrontShell>} />
      <Route path="/p/:slug" element={<StorefrontShell><CustomPage /></StorefrontShell>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/staff/login" element={<StaffLogin />} />

      {/* Developer */}
      <Route path="/developer" element={<ProtectedRoute roles={["developer"]}><DashboardLayout role="developer" /></ProtectedRoute>}>
        <Route index element={<DevOverview />} />
        <Route path="keys" element={<DevKeys />} />
        <Route path="limits" element={<DevLimits />} />
        <Route path="ai-usage" element={<DevAIUsage />} />
        <Route path="roles" element={<DevRoles />} />
        <Route path="users" element={<DevUsers />} />
        <Route path="branding" element={<DevBranding />} />
        <Route path="homepage" element={<DevHomepage />} />
        <Route path="menus" element={<DevMenus />} />
        <Route path="content" element={<DevContent />} />
        <Route path="pages" element={<DevPages />} />
        <Route path="maintenance" element={<DevMaintenance />} />
        <Route path="support" element={<DevSupport />} />
        <Route path="security" element={<DevSecurity />} />
        <Route path="audit" element={<DevAudit />} />
      </Route>

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute roles={["developer","admin"]}><DashboardLayout role="admin" /></ProtectedRoute>}>
        <Route index element={<AdmOverview />} />
        <Route path="orders" element={<AdmOrders />} />
        <Route path="products" element={<AdmProducts />} />
        <Route path="customers" element={<AdmCustomers />} />
        <Route path="reviews" element={<AdmReviews />} />
        <Route path="enquiries" element={<AdmEnquiries />} />
        <Route path="refunds" element={<AdmRefunds />} />
        <Route path="discounts" element={<AdmDiscounts />} />
        <Route path="categories" element={<AdmCategories />} />
        <Route path="popup" element={<AdmPopup />} />
        <Route path="developer-support" element={<AdmDeveloperSupport />} />
        <Route path="approvals" element={<AdmApprovals />} />
      </Route>

      {/* Shop assistant */}
      <Route path="/shop-dashboard" element={<ProtectedRoute roles={["developer","admin","shop_assistant"]}><DashboardLayout role="shop_assistant" /></ProtectedRoute>}>
        <Route index element={<ShopOverview />} />
        <Route path="add-product" element={<ShopAddProduct />} />
        <Route path="add-product-manual" element={<ShopAddProductManual />} />
        <Route path="camera" element={<ShopCameraForm />} />
        <Route path="categories" element={<AdmCategories />} />
        <Route path="my-products" element={<ShopMyProducts />} />
      </Route>

      <Route path="*" element={<StorefrontShell><div className="py-40 text-center"><h1 className="text-6xl font-display text-dark">404</h1><p className="text-gray-500 mt-4">Page not found</p></div></StorefrontShell>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SiteProvider>
          <LocaleProvider>
            <ThemeProvider>
              <CartProvider>
                <WishlistProvider>
                  <BrowserRouter>
                    <AppRoutes />
                    <Toaster theme="light" position="bottom-right" />
                  </BrowserRouter>
                </WishlistProvider>
              </CartProvider>
            </ThemeProvider>
          </LocaleProvider>
        </SiteProvider>
      </AuthProvider>
    </div>
  );
}
